import { FC, useMemo, useState } from 'react';

import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { PriceTableItem } from '@shared/api/priceTable/priceTable.interface';
import { Button } from '@shared/components/Button';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { exportPriceTableExcel } from '@shared/utils/priceTableExcel';
import { Checkbox, Table, TableColumnsType } from 'antd';

interface Props {
  items: PriceTableItem[];
  /** 프리드 수익금 컬럼 라벨 (기본 "프리드 수익금") */
  profitLabel?: string;
  /**
   * 원가(도매가) 표시 보정. 화환은 화환내역(정산 상세)과 동일하게
   * priceWhole 끝자리가 7500이면 포장비 2500을 더해 만원 단위로 맞춘다.
   * 미지정 시 원가를 그대로 사용. (useMemo 참조 안정성 위해 모듈 레벨 함수를 넘길 것)
   */
  adjustWhole?: (priceWhole: number) => number;
  /** 원가율 컬럼/요약 표시 여부 (기본 true, 화환은 false) */
  showCostRate?: boolean;
  /** 답례품 전용 유형(배송형/쿠폰형)·브랜드·선택형 컬럼 표시 여부 (기본 false) */
  showGiftColumns?: boolean;
  /**
   * 우리(마리화훼) 수수료율 %. FeeRate 방식일 때만 값을 주면 수수료 컬럼을 띄운다.
   * null/미지정(Rebate·화환)이면 수수료 컬럼 숨김.
   */
  feeRate?: number | null;
  /** 엑셀 파일명/시트명 (기본 "단가표") */
  tableName?: string;
}

/** 원가율(%) = 원가 / 판매가 × 100 — 판매가가 0 이하이면 계산 불가(null) */
const calcCostRate = (priceWhole: number, priceRetail: number): number | null =>
  priceRetail > 0 ? (priceWhole / priceRetail) * 100 : null;

const won = (v: number) => `${(v ?? 0).toLocaleString()}원`;

/** 기본 원가 보정 = 그대로 (안정적 참조용 모듈 레벨 상수) */
const identityWhole = (w: number) => w;

/**
 * 우리(마리화훼) 수수료(원) = 판매가 × feeRate%, 건별 올림(백엔드 ceilFeeWon과 동일).
 * 백엔드 freedProfit이 건별 내림(floorWon)이라, 올림 수수료여야
 * "판매가 − 원가 − 수수료 = 수익금"이 1원 오차 없이 맞는다. (−1e-6은 FP 잡음 가드)
 */
const calcFee = (priceRetail: number, feeRate: number) =>
  Math.ceil((priceRetail * feeRate) / 100 - 1e-6);

type Row = PriceTableItem & { index: number; costRate: number | null };

/**
 * 화환/답례품 단가표 공용 테이블.
 * 판매가·원가(도매가)·프리드(상조) 수익금을 보여준다. (원가율·유형·브랜드·수수료·선택형은 옵션)
 * 데이터는 백엔드가 전체 상품을 골라 freedProfit까지 계산해 내려준다(판매중/중지는 isEnabled).
 */
export const PriceTableView: FC<Props> = ({
  items,
  profitLabel = '프리드 수익금',
  adjustWhole = identityWhole,
  showCostRate = true,
  showGiftColumns = false,
  feeRate = null,
  tableName = '단가표',
}) => {
  //* 판매중지 포함 여부 (기본 미포함 — 판매중 상품만 표시)
  const [includeDisabled, setIncludeDisabled] = useState(false);

  const rows: Row[] = useMemo(() => {
    const result: Row[] = [];
    items.forEach((item) => {
      //* 판매중지 숨김 옵션
      if (!includeDisabled && item.isEnabled === false) return;
      //* 백엔드가 금액을 문자열로 내려줄 수 있어 숫자로 강제변환
      //* (문자열이면 priceWhole + 2500 이 "575002500" 처럼 연결돼버림)
      const priceRetail = Number(item.priceRetail) || 0;
      const priceWhole = adjustWhole(Number(item.priceWhole) || 0);
      const freedProfit = Number(item.freedProfit) || 0;
      result.push({
        ...item,
        priceRetail,
        priceWhole,
        freedProfit,
        index: result.length + 1,
        costRate: calcCostRate(priceWhole, priceRetail),
      });
    });
    return result;
  }, [items, adjustWhole, includeDisabled]);

  const enabledCount = rows.filter((r) => r.isEnabled).length;

  //* 평균 원가율 (계산 가능한 행만)
  const avgCostRate = useMemo(() => {
    const valid = rows.filter((r) => r.costRate != null);
    if (valid.length === 0) return null;
    return valid.reduce((sum, r) => sum + (r.costRate ?? 0), 0) / valid.length;
  }, [rows]);

  //* 선택형 펼침 상태 — 원가/프리드수익금의 (+) 버튼으로 토글 (기본 확장열은 숨김)
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const isExpandable = (r: Row) => !!r.isSelectable && (r.choiceUnits?.length ?? 0) > 0;
  const toggleExpand = (id: string) =>
    setExpandedKeys((keys) =>
      keys.includes(id) ? keys.filter((k) => k !== id) : [...keys, id],
    );
  const expandBtn = (r: Row) => (
    <button
      type="button"
      onClick={() => toggleExpand(r._id)}
      className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50"
    >
      <span className="text-sm leading-none">
        {expandedKeys.includes(r._id) ? '−' : '+'}
      </span>
      선택지별
    </button>
  );

  //* 선택형 상품 펼침 — 선택지별 실제 도매가/수익금
  const renderChoiceUnits = (r: Row) => (
    <div className="my-1 ml-10 overflow-hidden rounded-md border border-gray-200 bg-gray-50/60">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-xs text-gray-500">
            <th className="py-1.5 px-3 text-left font-medium">선택지</th>
            <th className="py-1.5 px-3 text-right font-medium">정가</th>
            <th className="py-1.5 px-3 text-right font-medium">원가(도매가)</th>
            <th className="py-1.5 px-3 text-right font-medium">{profitLabel}</th>
          </tr>
        </thead>
        <tbody>
          {(r.choiceUnits ?? []).map((cu) => (
            <tr key={cu._id} className="border-b border-gray-100 last:border-0">
              <td className="py-1.5 px-3">
                <div className="flex items-center gap-2">
                  {cu.imageUrl ? (
                    <img
                      src={fileBucketUrl(cu.imageUrl, 'w200')}
                      alt={cu.name}
                      className="h-8 w-8 shrink-0 rounded object-cover"
                    />
                  ) : null}
                  <span className="text-gray-800">{cu.name}</span>
                </div>
              </td>
              <td className="py-1.5 px-3 text-right text-gray-500">
                {cu.priceRegular == null ? '-' : won(Number(cu.priceRegular))}
              </td>
              <td className="py-1.5 px-3 text-right text-gray-600">
                {cu.priceWhole == null ? '-' : won(Number(cu.priceWhole))}
              </td>
              <td className="py-1.5 px-3 text-right font-medium text-rose-700">
                {cu.freedProfit == null ? '-' : won(Number(cu.freedProfit))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const allColumns: TableColumnsType<Row> = [
    {
      title: '번호',
      dataIndex: 'index',
      key: 'index',
      align: 'center',
      width: 64,
      render: (v: number) => <span className="font-medium text-gray-500">{v}</span>,
    },
    {
      title: '상품',
      key: 'product',
      render: (_, r) => (
        <div className="flex items-center gap-3">
          {r.imageUrl ? (
            <img
              src={fileBucketUrl(r.imageUrl, 'w200')}
              alt={r.name}
              className="h-12 w-12 shrink-0 rounded object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-gray-100 text-[10px] text-gray-400">
              없음
            </div>
          )}
          <span className="font-medium text-gray-900">{r.name}</span>
          {r.isSelectable ? (
            <span className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-600">
              선택형
            </span>
          ) : null}
        </div>
      ),
    },
    {
      title: '판매상태',
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      align: 'center',
      render: (v: boolean | undefined) =>
        v ? (
          <span className="rounded bg-green-50 px-2 py-0.5 text-xs font-medium text-green-600">
            판매중
          </span>
        ) : (
          <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
            판매중지
          </span>
        ),
    },
    {
      title: '유형',
      dataIndex: 'type',
      key: 'type',
      align: 'center',
      render: (v: GiftItemInfoType | undefined) =>
        v === GiftItemInfoType.Delivery ? (
          <span className="rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600">
            배송형
          </span>
        ) : v === GiftItemInfoType.Coupon ? (
          <span className="rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-600">
            쿠폰형
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      title: '브랜드',
      dataIndex: 'brandName',
      key: 'brandName',
      align: 'center',
      render: (v: string | undefined) =>
        v ? (
          <span className="text-gray-700">{v}</span>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      title: '판매가',
      dataIndex: 'priceRetail',
      key: 'priceRetail',
      align: 'right',
      sorter: (a, b) => a.priceRetail - b.priceRetail,
      render: (v: number) => <span className="text-gray-900">{won(v)}</span>,
    },
    {
      title: '원가(도매가)',
      dataIndex: 'priceWhole',
      key: 'priceWhole',
      align: 'right',
      sorter: (a, b) => a.priceWhole - b.priceWhole,
      //* 선택형은 대표값 대신 펼침 (+) 버튼 — 실제 도매가는 선택지별로
      render: (v: number, r) =>
        isExpandable(r) ? expandBtn(r) : <span className="text-gray-600">{won(v)}</span>,
    },
    {
      title: feeRate != null ? `마리화훼 수수료(${feeRate}%)` : '마리화훼 수수료',
      key: 'fee',
      align: 'right',
      render: (_, r) => (
        <span className="text-gray-500">
          {feeRate != null ? won(calcFee(r.priceRetail, feeRate)) : '-'}
        </span>
      ),
    },
    {
      title: profitLabel,
      dataIndex: 'freedProfit',
      key: 'freedProfit',
      align: 'right',
      sorter: (a, b) => a.freedProfit - b.freedProfit,
      //* 선택형은 대표값 대신 펼침 (+) 버튼 — 실제 수익금은 선택지별로
      render: (v: number, r) =>
        isExpandable(r) ? (
          expandBtn(r)
        ) : (
          <span className="font-bold text-rose-700">{won(v)}</span>
        ),
    },
    {
      title: '원가율',
      dataIndex: 'costRate',
      key: 'costRate',
      align: 'right',
      sorter: (a, b) => (a.costRate ?? -1) - (b.costRate ?? -1),
      render: (v: number | null) =>
        v == null ? (
          <span className="text-gray-400">-</span>
        ) : (
          <span className="text-gray-700">{v.toFixed(1)}%</span>
        ),
    },
  ];
  //* 컬럼 노출: 원가율(showCostRate)·유형/브랜드(showGiftColumns)·수수료(feeRate!=null)
  const columns = allColumns.filter((col) => {
    if (col.key === 'costRate') return showCostRate;
    if (col.key === 'type' || col.key === 'brandName') return showGiftColumns;
    if (col.key === 'fee') return feeRate != null;
    return true;
  });

  //* 화면에 보이는 행(필터·보정 반영) 그대로 엑셀로 내보낸다
  const handleExport = () =>
    exportPriceTableExcel(rows, {
      profitLabel,
      showCostRate,
      showGiftColumns,
      feeRate,
      fileName: tableName,
      sheetName: tableName,
    });

  return (
    <div className="space-y-3">
      {/* 요약 + 필터 + 엑셀 다운로드 (한 줄로 컴팩트하게) */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <span className="text-gray-600">
          상품 <span className="font-bold text-gray-900">{rows.length.toLocaleString()}</span>개
          <span className="ml-1.5 text-xs text-gray-400">
            (판매중 {enabledCount} · 중지 {rows.length - enabledCount})
          </span>
        </span>
        {showCostRate && (
          <span className="text-gray-600">
            평균 원가율{' '}
            <span className="font-bold text-gray-900">
              {avgCostRate == null ? '-' : `${avgCostRate.toFixed(1)}%`}
            </span>
          </span>
        )}
        <div className="ml-auto flex items-center gap-3">
          <Checkbox
            checked={includeDisabled}
            onChange={(e) => setIncludeDisabled(e.target.checked)}
          >
            <span className="text-sm text-gray-600">판매중지 포함</span>
          </Checkbox>
          <Button
            className="filled-gray-800 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-bold shadow-none"
            onClick={handleExport}
            disabled={rows.length === 0}
          >
            <p className="text-white">엑셀 다운로드</p>
          </Button>
        </div>
      </div>
      {/* 테이블 */}
      <div className="rounded-lg bg-white px-4 py-2">
        <Table
          className="w-full"
          columns={columns}
          dataSource={rows}
          rowKey="_id"
          rowClassName={(r) => (r.isEnabled ? '' : 'bg-gray-50 text-gray-400')}
          expandable={
            showGiftColumns
              ? {
                  expandedRowRender: renderChoiceUnits,
                  expandedRowKeys: expandedKeys,
                  onExpandedRowsChange: (keys) => setExpandedKeys(keys as string[]),
                  showExpandColumn: false, // 기본 확장 caret 숨김 — (+) 버튼으로 토글
                }
              : undefined
          }
          bordered
          size="small"
          pagination={{
            position: ['bottomCenter'],
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
          }}
        />
      </div>
    </div>
  );
};

export default PriceTableView;
